import { UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import { useRouter } from "next/compat/router";
import posthog from "posthog-js";
import { type PropsWithChildren, useEffect, useRef } from "react";
import { Toaster } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { SWRConfig } from "swr";

import Modals from "./Modals";
import Onboarding from "./Onboarding";
import TrackingProvider from "./TrackingProvider";
import { useGlobalStore, useLazyIntercom, useUserAction } from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";
import {
	CONVERSION_FLAGS,
	isConversionFlagOn,
} from "~/lib/conversion-test-flags";
import { isProgramInvitationPath } from "~/lib/programs/isProgramInvitationPath";
import { userActionAfterAccountCreated } from "~/lib/signup-modal.lib";
import type { CustomAppProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

const PageConfig = ({
	children,
	pageProps,
}: PropsWithChildren<CustomAppProps>) => {
	const { fallback, breadcrumbs } = pageProps;
	const { userAction, resetUserAction, setUserAction } = useUserAction();
	const didOpenMembershipAfterSignup = useRef(false);
	const trackCTA = useTrackCTA();
	const { userId } = useAuth();
	const { boot } = useLazyIntercom();

	const {
		setIsGoogleSignInSupported,
		hasScrolled,
		setHasScrolled,
		hasTimeOnSite,
		setHasTimeOnSite,
	} = useGlobalStore();

	useEffect(() => {
		boot();
	}, [boot]);

	const router = useRouter();
	const path = router?.asPath.split("?")[0];

	useEffect(() => {
		const userAgent = window.navigator.userAgent;
		const url = window.location.href;

		// List of social media in-app browsers to detect
		const socialApps = [
			"LinkedInApp",
			"FBAN",
			"Instagram",
			"Snapchat",
			"Twitter",
		];

		// Detect iOS (iPhone/iPad) social media in-app browser and redirect to Safari
		if (
			userAgent.includes("Mobile") &&
			(userAgent.includes("iPhone") || userAgent.includes("iPad")) &&
			socialApps.some((app) => userAgent.includes(app))
		) {
			setIsGoogleSignInSupported(false);
			window.location.href = `x-safari-${url}`;
			return;
		}

		// Detect Android social media in-app browser and redirect to Chrome
		if (
			userAgent.includes("Android") &&
			socialApps.some((app) => userAgent.includes(app))
		) {
			setIsGoogleSignInSupported(false);
			window.location.href = `googlechrome://${url}`;
			return;
		}
	}, [setIsGoogleSignInSupported]);

	useEffect(() => {
		if (hasScrolled) return;

		const onScroll = () => {
			const scrollableHeight =
				document.documentElement.scrollHeight - window.innerHeight;
			if (scrollableHeight > 0 && window.scrollY / scrollableHeight >= 0.5) {
				setHasScrolled(true);
			}
		};

		window.addEventListener("scroll", onScroll);

		return () => window.removeEventListener("scroll", onScroll);
	}, [hasScrolled, setHasScrolled]);

	useEffect(() => {
		if (hasTimeOnSite) return;

		const timer = setTimeout(() => {
			setHasTimeOnSite(true);
		}, 15_000);

		return () => clearTimeout(timer);
	}, [hasTimeOnSite, setHasTimeOnSite]);

	useEffect(() => {
		if (didOpenMembershipAfterSignup.current) return;

		const nextAction = userActionAfterAccountCreated({
			userAction,
			isSignedIn: !!userId,
			path,
		});
		if (nextAction !== UserAction.BECOME_A_MEMBER) return;

		didOpenMembershipAfterSignup.current = true;
		if (
			isConversionFlagOn(
				posthog.getFeatureFlag(CONVERSION_FLAGS.conversionTracking),
			)
		) {
			trackCTA(UserAction.BECOME_A_MEMBER, "After Sign Up");
		}
		void resetUserAction().then(() => {
			setUserAction(nextAction);
		});
	}, [path, resetUserAction, setUserAction, trackCTA, userAction, userId]);

	// Invitees only need to accept the invitation, so just close the sign-up modal
	useEffect(() => {
		if (
			userAction === UserAction.SIGN_UP &&
			userId &&
			isProgramInvitationPath(path)
		) {
			resetUserAction();
		}
	}, [path, resetUserAction, userAction, userId]);

	return (
		<SWRConfig
			value={{
				fetcher,
				fallback: fallback ?? {},
			}}
		>
			<TrackingProvider>
				<Onboarding key={userId}>
					<div className={clsx(`bg-white transition-all dark:bg-slate-800`)}>
						{children}
					</div>

					<Toaster
						toastOptions={{
							className: `dark:bg-slate-700 !rounded-full !max-w-xl`,
							position: "bottom-center",
						}}
					/>

					<Modals breadcrumbs={breadcrumbs} />
				</Onboarding>
				<Tooltip
					id="tooltip"
					className="z-20 rounded-lg! px-2! py-1! dark:bg-white dark:text-black [&>div]:text-inherit"
				/>
			</TrackingProvider>
		</SWRConfig>
	);
};

export default PageConfig;
